/**
 * Shared gate for the on-demand `POST /admin/channels/update-instantly` action
 * (GGC2-58 widened this from TikTok/Instagram-only to 12 platforms — see
 * ggc-be's docs/GGC2-58-channel-update-times-and-campaign-refresh.md).
 *
 * This draws from a real, unmetered, company-wide shared vendor-API quota,
 * and the BE endpoint has no dedup (see
 * docs/instant-channel-update-implementation-plan-20260801.md) — repeated
 * clicks each enqueue an independent request. A UI offering this action MUST
 * pair it with an actual disable gate, not just a warning; this cooldown is
 * that gate, factored out so every entry point (the panel page's own button,
 * `RefreshChannelStatsPopover`'s bulk refresh, `ColumnUpdatedAt`'s per-row
 * button) enforces the same 30-minute window instead of drifting apart.
 */

/** Platforms `update-instantly` accepts (GGC2-58; was TikTok/Instagram only). */
export const INSTANT_UPDATE_PLATFORMS = new Set([
  'youtube', 'twitch', 'tiktok', 'douyin', 'facebook', 'instagram',
  'twitter', 'discord', 'telegram', 'bilibili', 'chzzk', 'afreeca',
]);

export const INSTANT_UPDATE_COOLDOWN_MINUTES = 30;

/**
 * `elapsedMs === null` means "no usable freshness signal" (never updated, or
 * an unparseable/future timestamp) — always treated as "not in cooldown".
 * This matters because a channel the vendor reports as gone gets its
 * "updated at" stamped far in the future by the BE (so the periodic updater
 * never re-selects it); reading that sentinel as "just updated" would
 * permanently disable the one button that helps investigate it.
 */
export function instantUpdateCooldownGate(
  lastUpdatedIso?: string | null,
  cooldownMinutes: number = INSTANT_UPDATE_COOLDOWN_MINUTES,
): { allowed: boolean; elapsedMs: number | null } {
  if (!lastUpdatedIso) return { allowed: true, elapsedMs: null };
  const elapsedMs = Date.now() - new Date(lastUpdatedIso).getTime();
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return { allowed: true, elapsedMs: null };
  return { allowed: elapsedMs >= cooldownMinutes * 60_000, elapsedMs };
}
